'use client';

import { useEffect, useRef, useState } from 'react';
import { Button } from '@nf/ui';
import { DropdownIcon, TicketRunningIcon } from '@/components/icons/svg-icons';
import { CricketMatchModel } from '@nf/types/odds';
import { CricketLiveScore } from '@nf/types/scoreboard';
import { scoreboardService } from '@/services/scoreboard.service';
import { ScoreboardData } from '@/types/scoreboard-settings';
import { ScoreboardColumn } from './scoreboard-column';
import { usePrevious } from '@nf/utils-common/hooks';
import { getHomeTeamName, getTeamName } from '@/utils/odds-utils';
import { useTranslations } from 'next-intl';
import { useAtomValue } from 'jotai';
import { oddsPageAtom } from '@/atoms/odds-page';
import { Styles } from './scoreboard.style';
import { cx } from 'styled-system/css';

interface CricketInnsStatsProps {
	column: {
		type: string;
		round?: number;
		keyValue?: Array<string>;
	}[]
}
interface CricketBatsmanProps {
	column: {
		type: string;
		keyValue?: Array<string>;
		key: string;
	}[]
}

interface CricketBowlerProps {
	column: {
		type: string;
		keyValue?: Array<string>;
		key: string;
	}[]
}


const hideColumn = (round: number, ls: CricketLiveScore) => {
	switch (round) {
		case 2:
			return ls._gs !== '1';
	}

	return false;
}

const hideInfo = (type: string, ls: CricketLiveScore, keyValue?: Array<string>) => {
	switch (type) {
		case 'hasPlay':
			if (keyValue?.includes('h1e') && keyValue?.includes('a1e')) {
				return ls.hif === '1';
			} else if (keyValue?.includes('h2e') && keyValue?.includes('a2e')) {
				return ls.hif === '1';
			}
			break;
		case 'info':
		case 'oneInfo':
			return ls.hif === '1';
	}

	return false;
}

const getTitle = (type: string, round?: number, key?: string) => {
	let resourceKey = '';
	if (type === 'hasPlay') {
		switch (round) {
			case 1:
				switch (key) {
					case 'inh1':
						resourceKey = 'lbl_Crckt_Runs';
						break;
					case 'wh1':
						resourceKey = 'lbl_Crckt_Wkt';
						break;
					case 'h14':
						resourceKey = 'lbl_Crckt_4S';
						break;
					case 'h16':
						resourceKey = 'lbl_Crckt_6S';
						break;
					case 'h1e':
						resourceKey = 'lbl_Crckt_Extras';
						break;
				}
				break;
			case 2:

				switch (key) {
					case 'inh2':
						resourceKey = 'lbl_Crckt_Runs';
						break;
					case 'wh2':
						resourceKey = 'lbl_Crckt_Wkt';
						break;
					case 'h24':
						resourceKey = 'lbl_Crckt_4S';
						break;
					case 'h26':
						resourceKey = 'lbl_Crckt_6S';
						break;
					case 'h2e':
						resourceKey = 'lbl_Crckt_Extras';
						break;

				}
				break;
		}
	} else if (type === 'OngoingInfo') {
		switch (round) {
			case 1:
			case 2:
				resourceKey = 'lbl_Crckt_Overs';
				break;
		}
	} else if (type === 'info') {
		switch (key) {
			case 'bat':
				resourceKey = 'lbl_Crckt_Runs';
				break;
			case 'b1b':
				resourceKey = 'lbl_Crckt_Balls';
				break;
			case 'sr':
				resourceKey = 'lbl_Crckt_SR';
				break;
			case 'b14':
				resourceKey = 'lbl_Crckt_4S';
				break;
			case 'b16':
				resourceKey = 'lbl_Crckt_6S';
				break;
		}
	} else if (type === 'oneInfo') {
		switch (key) {
			case 'bwo':
				resourceKey = 'lbl_Crckt_Overs';
				break;
			case 'bwm':
				resourceKey = 'lbl_Crckt_Maidens';
				break;
			case 'bwr':
				resourceKey = 'lbl_Crckt_Runs';
				break;
			case 'bww':
				resourceKey = 'lbl_Crckt_Wkt';
				break;
			case 'bwer':
				resourceKey = 'lbl_Crckt_ER';
				break;
		}
	}
	return resourceKey;
}

const getCricketOversBall = (liveScore: CricketLiveScore) => {
	let oversBall = '-';
	if (liveScore.hls === '0') {
		oversBall = `${liveScore.over}.${liveScore.ball}*`;
	}
	if (liveScore.hls === '3') {
		oversBall = `${liveScore.over}`;
	}
	return oversBall;
};

//OngoingInfo round-2
const getKeyValue = (liveScore: CricketLiveScore) => {
	return getCricketOversBall(liveScore);
};

// 板球
export const ScoreboardSp50 = ({ cricketMatchModel }: { cricketMatchModel: CricketMatchModel }) => {
	const t = useTranslations('General');
	const matchStatus = t('lbl_Crckt_Team');
	const [isOpen, setisOpen] = useState(false);

	const ls = cricketMatchModel.ls;
	const cricketScoreboardSetting = scoreboardService.getScoreboardSetting(cricketMatchModel.GameID).boardColumn;
	const cricketScoreboardOption = scoreboardService.getScoreboardSetting(cricketMatchModel.GameID).options;
	const cricketScoreboardDetails = scoreboardService.getScoreboardSetting(cricketMatchModel.GameID).details;
	const cricketScoreboardData: ScoreboardData[] = [];
	const trn = ls[cricketScoreboardOption.trn as keyof CricketLiveScore];
	const bgn = ls[cricketScoreboardOption.bgn as keyof CricketLiveScore];
	cricketScoreboardSetting.forEach((setting) => {
		if (hideColumn(setting.round ?? 0, ls)) {
			return;
		}

		const { round, keyValue, type } = setting;
		switch (setting.type) {
			case 'hasPlay':
				if (parseInt(ls.llp, 10) >= (round ?? 0) && cricketScoreboardOption && trn && bgn) {
					let hasPlay = false;
					if (round) {
						hasPlay = parseInt(ls.llp, 10) > round;
					}
					const homeScore = hasPlay || trn === '1' || bgn === '1' ?
						ls[keyValue?.[0] as keyof CricketLiveScore] : '-';
					const awayScore = hasPlay || trn === '2' || bgn === '2' ?
						ls[keyValue?.[1] as keyof CricketLiveScore] : '-';

					cricketScoreboardData.push({
						heading: t(getTitle(type, setting.round ?? 0, setting.keyValue?.[0])),
						cells: [
							{ text: homeScore },
							{ text: awayScore }
						]
					});
				}
				break;
			case 'OngoingInfo':
				const scoreArray = ['-', '-'];
				if (cricketScoreboardOption && trn && parseInt(ls.llp, 10) === round) {
					const showIndex = parseInt(trn);
					scoreArray[showIndex - 1] = getKeyValue(ls);
				}
				cricketScoreboardData.push({
					heading: t(getTitle(setting.type, setting.round ?? 0)),
					cells: [
						{ text: scoreArray[0] }, //home
						{ text: scoreArray[1] } //away
					]
				});
				break;
		}
	});

	return (
		<>
			<div className={cx('scoreboard scoreboard--sport50', Styles.scoreboard)}>
				<div className="scoreboard__row">
					<div className="scoreboard__col-event">
						<div className="scoreboard__col">
							<div className="scoreboard__cell-heading" />
						</div>
					</div>
					<div className="scoreboard__col-event">
						<div className="scoreboard__col">
							<div className="scoreboard__cell-heading">
								<span className="scoreboard__text">{ls._gs === '1' ? t('lbl_Crckt_1InnsR') : t('lbl_Crckt_Inns')}</span>
							</div>
						</div>
					</div>
					{
						ls._gs === '1' ? (
							<div className="scoreboard__col-event">
								<div className="scoreboard__col">
									<div className="scoreboard__cell-heading">
										<span className="scoreboard__text">{t('lbl_Crckt_2InnsR')}</span>
									</div>
								</div>
							</div>
						) : null
					}
				</div>
				<div className="scoreboard__row-inning">
					<div className="scoreboard__col-event">
						<div className="scoreboard__cell-heading">
							<span className="scoreboard__text">{matchStatus}</span>
						</div>
						<div className="scoreboard__cell" data-current={trn === '1'}>
							<TicketRunningIcon />
							<span className="scoreboard__text">{t('lbl_ShortH')}</span>
						</div>
						<div className="scoreboard__cell" data-current={trn === '2'}>
							<TicketRunningIcon />
							<span className="scoreboard__text">{t('lbl_ShortA')}</span>
						</div>
					</div>
					{
						cricketScoreboardData.map((column, index) => {
							return (
								<div key={`${column.cells}${index}`} className="scoreboard__col">
									<ScoreboardColumn
										key={`${index}${cricketMatchModel.MatchId}Column`}
										heading={column.heading}
										cells={column.cells}
									/>
								</div>
							);
						})}
				</div>
				<div
					className="scoreboard__detail"
					data-active={isOpen}
				>
					<CricketInnsStats settingsInnsStatsDetails={cricketScoreboardDetails?.innsStats} matchModel={cricketMatchModel} trn={trn} bgn={bgn} />
					<CricketBatsman settingsBatsmanDetails={cricketScoreboardDetails?.batsman} matchModel={cricketMatchModel} />
					<CricketBowler settingsBowlerDetails={cricketScoreboardDetails?.bowler} matchModel={cricketMatchModel} />
					<div className="scoreboard__row-inning">
						<CricketLastSixBalls matchModel={cricketMatchModel} />
						<CricketLastWktRuns key={cricketMatchModel.MatchId} matchModel={cricketMatchModel} />
					</div>
				</div>

				<div className="scoreboard__btn">
					<Button
						className="btn--icon"
						data-active={isOpen}
						onClick={() => setisOpen(!isOpen)}
					>
						<DropdownIcon />
					</Button>
				</div>
			</div>
		</>
	);
};

const CricketInnsStats = ({ settingsInnsStatsDetails, matchModel, trn, bgn }: { settingsInnsStatsDetails?: CricketInnsStatsProps, matchModel: CricketMatchModel, trn: string, bgn: string }) => {
	const cricketInnsStatsScoreboardData: ScoreboardData[] = [];
	const ls = matchModel.ls;
	const t = useTranslations('General');

	settingsInnsStatsDetails?.column.forEach((setting) => {
		let homeScore: string = '-';
		let awayScore: string = '-';

		if (hideColumn(setting.round ?? 0, ls)) {
			return;
		}
		if (hideInfo(setting.type, ls, setting.keyValue)) {
			cricketInnsStatsScoreboardData.push({
				heading: t(getTitle(setting.type, setting.round ?? 0, setting.keyValue?.[0])),
				cells: [{ text: homeScore }, { text: awayScore }]
			});
			return;
		}

		if (setting.type === 'hasPlay') {
			if (parseInt(ls.llp, 10) >= (setting.round ?? 0)) {
				let hasPlay = false;
				if (setting.round) {
					hasPlay = parseInt(ls.llp, 10) > setting.round;
				}
				homeScore = hasPlay || trn === '1' || bgn === '1' ?
					ls[setting.keyValue?.[0] as keyof CricketLiveScore] : '-';
				awayScore = hasPlay || trn === '2' || bgn === '2' ?
					ls[setting.keyValue?.[1] as keyof CricketLiveScore] : '-';

				cricketInnsStatsScoreboardData.push({
					heading: t(getTitle(setting.type, setting.round ?? 0, setting.keyValue?.[0])),
					cells: [{ text: homeScore }, { text: awayScore }]
				});
			} else {
				cricketInnsStatsScoreboardData.push({
					heading: t(getTitle(setting.type, setting.round ?? 0, setting.keyValue?.[0])),
					cells: [{ text: homeScore }, { text: awayScore }]
				});
			}
		}
	});

	const batsMan = t('lbl_Crckt_BM');

	return (
		<>
			<div className="scoreboard__row-inning">
				<div className="scoreboard__col-event">
					<div className="scoreboard__cell-heading">
						<span className="scoreboard__text"> {t('lbl_Crckt_InSt')}</span>
					</div>

					<div className="scoreboard__cell">
						<span
							className="scoreboard__text"
							data-default={`${batsMan} A`}
						>
							{t('lbl_ShortH')}
						</span>
					</div>

					<div className="scoreboard__cell">
						<span
							className="scoreboard__text"
							data-default={`${batsMan} B`}
						>
							{t('lbl_ShortA')}
						</span>
					</div>
				</div>
				{
					cricketInnsStatsScoreboardData.map((column, index) => {
						return (
							<div key={`${column.cells}${index}`} className="scoreboard__col">
								<ScoreboardColumn
									key={`${index}${matchModel.MatchId}Inns`}
									heading={column.heading}
									cells={column.cells}
								/>
							</div>
						);
					})
				}
			</div>
		</>
	)
};
const CricketBatsman = ({ settingsBatsmanDetails, matchModel }: { settingsBatsmanDetails?: CricketBatsmanProps, matchModel: CricketMatchModel }) => {
	const cricketBatsmanScoreboardData: ScoreboardData[] = [];
	const ls = matchModel.ls;
	const t = useTranslations('General');
	const oddsData = useAtomValue(oddsPageAtom);
	const batsmanA = getHomeTeamName(matchModel?.HID2, matchModel.Neu, t('Neutral_Team'), oddsData?.teamNameMap);
	const batsmanB = getTeamName(matchModel?.AID2, oddsData?.teamNameMap);

	if (ls.btrn === '0') {
		return null;
	}
	settingsBatsmanDetails?.column.forEach((setting) => {
		let homeScore: string = '-';
		let awayScore: string = '-';
		if (setting.key !== 'bat' && hideInfo(setting.type, ls)) {
			cricketBatsmanScoreboardData.push({
				heading: t(getTitle(setting.type, 0, setting.key)),
				cells: [{ text: homeScore }, { text: awayScore }]
			});
			return;
		}

		if (setting.type === 'info') {
			homeScore = ls[setting.keyValue?.[0] as keyof CricketLiveScore]
			awayScore = ls[setting.keyValue?.[1] as keyof CricketLiveScore]
		}

		cricketBatsmanScoreboardData.push({
			heading: t(getTitle(setting.type, 0, setting.key)),
			cells: [{ text: homeScore }, { text: awayScore }]
		});
	});

	const batsMan = t('lbl_Crckt_BM');

	return (
		<>
			<div className="scoreboard__row">
				<div className="scoreboard__col-event">
					<div className="scoreboard__cell-heading">
						<span className="scoreboard__text">{batsMan}</span>
					</div>

					<div className="scoreboard__cell" data-current={ls.btrn === '1'}>
						<TicketRunningIcon  />
						<span
							className="scoreboard__text"
							data-default={`${batsMan} A`}
						>
							{batsmanA.indexOf(matchModel?.HID2 + '') === -1 ? batsmanA : `${batsMan} A`}
						</span>
					</div>

					<div className="scoreboard__cell" data-current={ls.btrn === '2'}>
						<TicketRunningIcon  />
						<span
							className="scoreboard__text"
							data-default={`${batsMan} B`}
						>
							{batsmanB.indexOf(matchModel?.AID2 + '') === -1 ? batsmanB : `${batsMan} B`}
						</span>
					</div>
				</div>
				{
					cricketBatsmanScoreboardData.map((column, index) => {
						return (
							<div key={`${column.cells}${index}`} className="scoreboard__col">
								<ScoreboardColumn
									key={`${index}${matchModel.MatchId}Batsman`}
									heading={column.heading}
									cells={column.cells}
								/>
							</div>
						);
					})
				}
			</div>
		</>
	)
};

const CricketBowler = ({ settingsBowlerDetails, matchModel }: { settingsBowlerDetails?: CricketBowlerProps, matchModel: CricketMatchModel }) => {
	const cricketBowlerScoreboardData: ScoreboardData[] = [];
	const ls = matchModel.ls;
	const t = useTranslations('General');
	const oddsData = useAtomValue(oddsPageAtom);

	const bowlerA = ls.hif === '1' ? '-' : getHomeTeamName(Number(ls.bwl), matchModel.Neu, t('Neutral_Team'), oddsData?.teamNameMap);
	if (ls.hbw === '1') {
		return null;
	}

	settingsBowlerDetails?.column.forEach((setting) => {
		let BowlerScore: string = '-';
		if (hideInfo(setting.type, ls)) {
			cricketBowlerScoreboardData.push({
				heading: t(getTitle(setting.type, 0, setting.key)),
				cells: [{ text: BowlerScore }]
			});
			return;
		}

		if (setting.type === 'oneInfo') {
			BowlerScore = ls[setting.keyValue?.[0] as keyof CricketLiveScore]
		}

		cricketBowlerScoreboardData.push({
			heading: t(getTitle(setting.type, 0, setting.key)),
			cells: [{ text: BowlerScore }]
		});
	});

	return (
		<>
			<div className="scoreboard__row">
				<div className="scoreboard__col-event">
					<div className="scoreboard__cell-heading">
						<span className="scoreboard__text">{'lbl_Crckt_Bowler'}</span>
					</div>

					<div className="scoreboard__cell">
						<span
							className="scoreboard__text"
							data-default={'lbl_Crckt_Bowler'}
						>
							{bowlerA}
						</span>
					</div>
				</div>
				{
					cricketBowlerScoreboardData.map((column, index) => {

						return (
							<div key={`${column.cells}${index}`} className="scoreboard__col">
								<ScoreboardColumn
									key={`${index}${matchModel.MatchId}Bowler`}
									heading={column.heading}
									cells={column.cells}
								/>
							</div >
						);
					})
				}
			</div >
		</>
	)
};

const CricketLastSixBalls = ({ matchModel }: { matchModel: CricketMatchModel }) => {
	const t = useTranslations('General');
	const ls = matchModel.ls;
	const lastSixBall = [];
	for (let index = 1; index < 7; index += 1) {
		const score = (ls[`bal${index}` as keyof CricketLiveScore]) === '0' ? '' : ls[`bal${index}` as keyof CricketLiveScore]; // 0 is show empty
		lastSixBall.push(
			<span key={index} className="scoreboard-ball" >{score}</span>
		);
	}
	return (
		<>
			<div className="scoreboard__col">
				<div className="scoreboard__cell-heading">
					<span className="scoreboard__text">
						{t('lbl_Last6B')}
					</span>
				</div>
				<div className="scoreboard-ball-group">
					{lastSixBall}
				</div>
			</div>
		</>
	);
}

const CricketLastWktRuns = ({ matchModel }: { matchModel: CricketMatchModel }) => {
	const t = useTranslations('General');
	const ls = matchModel.ls;
	const score = Number(ls.lw) ?? 0;
	const previousPoint = usePrevious<number>(score) ?? 0;
	const [isScored, setIsScored] = useState(false);
	const timerRef = useRef<ReturnType<typeof setTimeout>>();
	useEffect(() => {
		const isPointChange = previousPoint !== Number(ls.lw)
		if (!isPointChange || previousPoint === 0) {
			return;
		}

		clearTimeout(timerRef.current);
		setIsScored(true);
		timerRef.current = setTimeout(() => {
			setIsScored(false);
		}, 3000);

		return () => {
			clearTimeout(timerRef.current);
		};
	}, [ls.lw]);

	return (
		<>
			<div className="scoreboard__col">
				<div className="scoreboard__cell-heading">
					<span className="scoreboard__text">
						{t('lbl_Crckt_LWcktR')}
					</span>
				</div>
				<div className="scoreboard__cell">
					<span className="scoreboard__text" data-scored={isScored} >{ls.lw}</span>
				</div>
			</div>

		</>
	);
}